import type { OnChangeFn, Row, SortingState } from "@tanstack/react-table"
import { getCoreRowModel } from "@tanstack/react-table"
import type { FC, Ref } from "react"

import { useChartOfAccountsColumns } from "../hooks/useChartOfAccountsColumns"

import { AccountDetailsRow } from "./AccountDetailsRow"

import { AerosBaseTable } from "@/components/Tables/Aeros/components/AerosBaseTable"
import type { GlAccount } from "@/graphql/codegen/graphql"

interface ChartOfAccountsTableProps {
  data: GlAccount[]
  sorting: SortingState
  onSortingChange: OnChangeFn<SortingState>
  loading: boolean
  error?: string | null
  scrollHandler: () => void
  tableContainerRef?: Ref<HTMLDivElement>
  onEdit?: (account: GlAccount) => void
}

export const ChartOfAccountsTable: FC<ChartOfAccountsTableProps> = ({
  data,
  sorting,
  onSortingChange,
  loading,
  error,
  scrollHandler,
  tableContainerRef,
  onEdit,
}) => {
  const columns = useChartOfAccountsColumns({ onEdit })

  const renderExpandedContent = (row: Row<GlAccount>) => <AccountDetailsRow account={row.original} />

  return (
    <AerosBaseTable<GlAccount>
      error={error ? { message: error } : undefined}
      features={{
        table: {
          stickyHeader: true,
        },
        tanstackOptions: {
          data,
          columns,
          state: {
            sorting,
            isLoading: loading,
          },
          manualSorting: true,
          enableSorting: true,
          enableMultiSort: false,
          enableSortingRemoval: false,
          getCoreRowModel: getCoreRowModel(),
          getRowId: (row) => row.glAccountId || "unknown id",
          onSortingChange,
          getRowCanExpand: () => true,
          renderExpandedContent,
        },
      }}
      slotProps={{
        container: {
          ref: tableContainerRef,
          onScroll: scrollHandler,
          className: "overflow-auto",
        },
        bodyRow: {
          className: "hover:bg-gray-50",
        },
        bodyCellExpanded: {
          className: "bg-primary-100",
        },
        expandedCell: {
          className: "p-0",
        },
      }}
      noDataMessage='No accounts found'
    />
  )
}
